import React, { useEffect, useMemo, useState } from 'react';
import ConceptHeader from '../CalculationResults/ConceptHeader';
import ParameterCard from '../CalculationResults/ParameterCard';
import ModelDataDocumentationModal from '../ModelDataDocumentationModal';
import OperationalStatementGrid, {
    Granularity,
    VarianceStyle,
} from './OperationalStatementGrid';
import { TailwindSelect } from '../Tailwind';
import { cn } from '../../lib/utils';

interface OperationalDetailPanelProps {
    selectedItem: any | null;
    operationalItem: any | null;
    tree: { items: Record<string, any>; rootId: string };
    state: any;
    reloadState: () => void;
    userRights: any;
    onSelectItem: (id: string) => void;
}

/** Small segmented control matching the webapp's primary-accent design language. */
function Segmented<T extends string>({
    value,
    options,
    onChange,
    size = 'md',
}: {
    value: T;
    options: { value: T; label: string; icon?: string }[];
    onChange: (v: T) => void;
    size?: 'sm' | 'md';
}) {
    return (
        <div className="inline-flex items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-100 p-0.5">
            {options.map((o) => {
                const active = value === o.value;
                return (
                    <button
                        key={o.value}
                        onClick={() => onChange(o.value)}
                        className={cn(
                            'inline-flex items-center gap-1.5 rounded-md font-semibold transition-all',
                            size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
                            active ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-800',
                        )}
                    >
                        {o.icon && <span className="material-symbols-outlined text-[16px]">{o.icon}</span>}
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

export default function OperationalDetailPanel({
    selectedItem,
    operationalItem,
    tree,
    state,
    reloadState,
    userRights,
    onSelectItem,
}: OperationalDetailPanelProps) {
    const [docModalOpen, setDocModalOpen] = useState(false);

    // View controls — persist across item selection.
    const [granularity, setGranularity] = useState<Granularity>('month');
    const [varianceStyle, setVarianceStyle] = useState<VarianceStyle>('delta');
    const [editedCells, setEditedCells] = useState<Set<string>>(() => new Set());

    // Scalar fields (float, grid) — read-only forecast values.
    const parameterFields = useMemo(() => {
        if (!selectedItem) return [];
        return Object.entries(selectedItem.fields || {}).filter(
            ([_, field]: [string, any]) => field.type !== 'time_serie' && field.type !== 'dynamic_grid',
        );
    }, [selectedItem]);

    // Time serie fields — the statement's line items.
    const timeSerieFields = useMemo(() => {
        if (!selectedItem) return [];
        return Object.entries(selectedItem.fields || {}).filter(
            ([_, field]: [string, any]) => field.type === 'time_serie',
        ) as [string, any][];
    }, [selectedItem]);

    // Reset the session edit-count when switching items.
    useEffect(() => {
        setEditedCells(new Set());
    }, [selectedItem?.id]);

    const handleEdit = (key: string) => setEditedCells((prev) => new Set(prev).add(key));

    // Empty state
    if (!selectedItem) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                    <span className="material-symbols-outlined text-slate-400 text-[32px]">monitoring</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Select an item</h3>
                <p className="text-sm text-slate-500 max-w-xs">
                    Pick a statement in the tree to read its modelized plan against the realized results.
                </p>
            </div>
        );
    }

    const hasStatement = timeSerieFields.length > 0;

    return (
        <div className="h-full overflow-y-auto">
            {/* Header with breadcrumbs */}
            <ConceptHeader
                item={selectedItem}
                tree={tree}
                onBreadcrumbClick={onSelectItem}
                onDocumentationClick={() => setDocModalOpen(true)}
            />

            {/* Scalar fields (read-only) */}
            {parameterFields.length > 0 && (
                <div className="px-6 py-4">
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Parameters</h3>
                    <ParameterCard
                        item={selectedItem}
                        fields={parameterFields}
                        reloadState={reloadState}
                        currentState={state}
                        userRights={userRights}
                    />
                </div>
            )}

            {/* Statement: one continuous modelized-vs-realized view */}
            {hasStatement && (
                <div className="px-6 pb-8 pt-2">
                    {/* Toolbar */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                        <div className="hidden sm:flex items-center gap-2 text-[13px] font-semibold text-slate-600">
                            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-slate-400 to-primary" />
                            Modelized vs realized
                            {editedCells.size > 0 && (
                                <span className="rounded-full bg-primary/10 text-primary text-[11px] font-bold px-2 py-0.5">
                                    {editedCells.size} edited
                                </span>
                            )}
                            {granularity !== 'month' && (
                                <span className="text-slate-400 font-medium">· {granularity} entries split evenly across months</span>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            <div className="w-[190px]">
                                <TailwindSelect
                                    value={varianceStyle}
                                    onChange={(v: string) => setVarianceStyle(v as VarianceStyle)}
                                    options={[
                                        { value: 'delta', label: 'Realized + delta' },
                                        { value: 'heatmap', label: 'Heatmap' },
                                    ]}
                                />
                            </div>
                            <Segmented<Granularity>
                                size="sm"
                                value={granularity}
                                onChange={setGranularity}
                                options={[
                                    { value: 'month', label: 'Month' },
                                    { value: 'quarter', label: 'Quarter' },
                                    { value: 'year', label: 'Year' },
                                ]}
                            />
                        </div>
                    </div>

                    {/* Grid */}
                    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        <OperationalStatementGrid
                            fields={timeSerieFields}
                            operationalItem={operationalItem}
                            userRights={userRights}
                            reloadState={reloadState}
                            granularity={granularity}
                            varianceStyle={varianceStyle}
                            editedCells={editedCells}
                            onEdit={handleEdit}
                        />
                    </div>
                </div>
            )}

            {/* Documentation Modal */}
            <ModelDataDocumentationModal
                item={selectedItem}
                variant="Output"
                open={docModalOpen}
                setOpen={setDocModalOpen}
            />
        </div>
    );
}
